import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { _js } from '@ifixit/localize';
import { useQuery } from '@tanstack/react-query';
import { OrdersTable } from './orders_table';
import { OrderType, TrackingType } from './orders_types';

type ApiOrder = {
   id: number;
   name: string;
   number: number;
   storeCode: string;
   nextLocale: string;
   processedAt: string;
   cancelledAt: string | null;
   statusPageUrl: string;
   displayFulfillmentStatus: string;
   totalPrice: {
      amount: string;
      currencyCode: string;
   };
   fulfillments: {
      trackingInfo: {
         company: string | null;
         number: string | null;
         url: string | null;
      }[];
   }[];
   lineItems: {
      quantity: number;
      sku: string | null;
      imageUrl: string | null;
   }[];
};

type OrderHistoryResponse = {
   orders: ApiOrder[];
};

type ServerSideData = {
   orders: OrderType[];
   fetchShopifyOrders: boolean;
   storeName: string;
   isUS: boolean;
};

export function OrderHistory({ data }: { data: ServerSideData }) {
   const apiClient = useIFixitApiClient();

   const ordersQuery = useQuery({
      queryKey: ['order-history'],
      queryFn: () =>
         apiClient
            .get<OrderHistoryResponse>('store/user/order-history', {
               statName: 'order-history',
            })
            .then(response => formatOrderData(response.orders)),
      enabled: data.fetchShopifyOrders,
      staleTime: Infinity,
      retry: false,
   });

   // Use Shopify data if available, otherwise use server-side data
   const orders: OrderType[] = data.fetchShopifyOrders ? ordersQuery.data || [] : data.orders || [];

   return ordersQuery.isError ? (
      <ErrorMessage />
   ) : ordersQuery.isLoading && data.fetchShopifyOrders ? (
      <LoadingMessage />
   ) : orders && orders.length > 0 ? (
      <OrdersDisplay orders={orders} />
   ) : (
      <NoOrdersMessage storeName={data.storeName} />
   );
}

export function formatOrderData(orders: ApiOrder[]): OrderType[] {
   return orders.map((order: ApiOrder) => {
      const trackingInfo: TrackingType[] = [];
      order.fulfillments.forEach(fulfillment => {
         fulfillment.trackingInfo.forEach(tracking => {
            trackingInfo.push({
               carrier: tracking.company ?? '',
               number: tracking.number ?? '',
               link: tracking.url ?? '',
            });
         });
      });
      const [stepNumber, widthPercent] = getShopifyOrderStep(order);
      const thumbnail = order.lineItems[0]?.imageUrl ?? undefined;
      const item_count = order.lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      return {
         orderid: order.number,
         currentStep: stepNumber,
         invoice_link: order.statusPageUrl,
         invoiceLink: order.statusPageUrl,
         reviewLink: orderReviewLink(order),
         order_name: order.name,
         status: getDetailedStatus(order),
         orderStatus: getDetailedStatus(order),
         widthPercent: widthPercent,
         isReviewable: isSuccessfullyFulfilled(order),
         init_date: Date.parse(order.processedAt) / 1000,
         total: Number(order.totalPrice.amount),
         currency_type: order.totalPrice.currencyCode,
         shipment_tracking: trackingInfo,
         shopifyName: order.name,
         showTracking: order.fulfillments.length > 0,
         thumbnail,
         item_count,
      };
   });
}

const OrdersDisplay = ({ orders }: { orders: OrderType[] }) => (
   <div className="flex flex-col flex-1">
      <OrdersTable orders={orders} />
   </div>
);

const NoOrdersMessage = ({ storeName }: { storeName: string }) => (
   <p>{_js('You have no orders with iFixit %1', storeName)}</p>
);

const ErrorMessage = () => (
   <p>
      {_js("We're having trouble getting your orders. Please try again later or contact support")}
   </p>
);

const LoadingMessage = () => <p>{_js('Loading...')}</p>;

const step_2 = new Set([
   'UNFULFILLED',
   'RESTOCKED',
   'OPEN',
   'PENDING_FULFILLMENT',
   'PARTIALLY_FULFILLED',
   'IN_PROGRESS',
   'SCHEDULED',
   'REQUEST_DECLINED',
]);
const step_3 = new Set(['FULFILLED']);

function firstTrackingCompany(order: ApiOrder): string | null {
   return order.fulfillments[0]?.trackingInfo[0]?.company ?? null;
}

function getShopifyOrderStep(order: ApiOrder): number[] {
   if (step_2.has(order.displayFulfillmentStatus)) {
      return [2, 35];
   } else if (
      step_3.has(order.displayFulfillmentStatus) &&
      (order.fulfillments.length === 0 || firstTrackingCompany(order) == null)
   ) {
      return [3, 70];
   } else if (order.fulfillments.length > 0) {
      return [4, 100];
   }
   return [1, 0];
}

function getDetailedStatus(order: ApiOrder): string {
   if (
      step_3.has(order.displayFulfillmentStatus) &&
      (order.fulfillments.length === 0 || firstTrackingCompany(order) == null)
   ) {
      return _js('Packed');
   } else if (order.fulfillments.length > 0) {
      return _js('Shipped');
   }
   return getLocalizedFulfillmentStatus(order.displayFulfillmentStatus);
}

function isSuccessfullyFulfilled(order: ApiOrder): boolean {
   return (
      order.cancelledAt == null &&
      order.displayFulfillmentStatus === 'FULFILLED' &&
      order.fulfillments.length > 0 &&
      firstTrackingCompany(order) != null
   );
}

function orderReviewLink(order: ApiOrder): string {
   const path = `/User/Reviews/order/${order.nextLocale}/${order.id}`;
   const url = new URL(path, window.origin);
   const skus = order.lineItems
      .map(item => item.sku)
      .filter(Boolean)
      .join(',');
   const params = new URLSearchParams({ skus });
   return `${url.origin}${url.pathname}?${params}`;
}

const fulfillmentStatusMapping: { [key: string]: string } = {
   FULFILLED: _js('Fulfilled'),
   IN_PROGRESS: _js('In Progress'),
   ON_HOLD: _js('On Hold'),
   OPEN: _js('Open'),
   PARTIALLY_FULFILLED: _js('Partially Fulfilled'),
   PENDING_FULFILLMENT: _js('Pending Fulfillment'),
   REQUEST_DECLINED: _js('Request Declined'),
   RESTOCKED: _js('Restocked'),
   SCHEDULED: _js('Scheduled'),
   UNFULFILLED: _js('Unfulfilled'),
};

function getLocalizedFulfillmentStatus(status: string): string {
   return fulfillmentStatusMapping[status] ?? status;
}
